<template>
  <div class="quality-report-page">
    <div class="report-sheet">
      <header class="report-header">
        <b-row align-v="center">
          <b-col cols="3">
            <img :src="logoSrc" alt="Logo" class="report-logo">
          </b-col>

          <b-col cols="9" class="text-right">
            <h1 class="report-title">Relatório de Indicadores de Qualidade</h1>
            <div class="report-subtitle">
              Painel demonstrativo de apontamentos, riscos e anomalias
            </div>
          </b-col>
        </b-row>
      </header>

      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">1. Visão Geral dos Apontamentos</h2>
        </div>

        <div class="section-body">
          <div class="summary-grid">
            <div class="summary-box">
              <div class="summary-label">Total de apontamentos</div>
              <div class="summary-value">{{ overview.total }}</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Apontamentos resolvidos</div>
              <div class="summary-value">{{ overview.resolved }}</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Apontamentos em aberto</div>
              <div class="summary-value">{{ overview.open }}</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Taxa de resolução</div>
              <div class="summary-value">{{ overview.rate }}</div>
              <div class="summary-note">Percentual geral de itens concluídos</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Tempo médio de resolução</div>
              <div class="summary-value">{{ overview.avgDays }}</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Tempo médio de resolução (críticos)</div>
              <div class="summary-value">{{ overview.avgCriticalDays }}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">2. Distribuição por Grau de Risco</h2>
        </div>

        <div class="section-body">
          <b-row>
            <b-col lg="6" cols="12" class="mb-3 mb-lg-0">
              <div class="chart-box">
                <apexchart
                    type="pie"
                    height="300"
                    :options="riskChartOptions"
                    :series="riskChartSeries"
                />
              </div>
            </b-col>

            <b-col lg="6" cols="12">
              <div class="table-responsive">
                <table class="table table-bordered align-middle mb-0">
                  <thead>
                  <tr>
                    <th>Classificação</th>
                    <th class="text-right">Quantidade</th>
                    <th class="text-right">%</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in riskTable" :key="item.name">
                    <td>
                      <span class="mini-badge" :class="item.badgeClass">{{ item.name }}</span>
                    </td>
                    <td class="text-right">{{ item.value }}</td>
                    <td class="text-right">{{ item.percent }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </b-col>
          </b-row>
        </div>
      </section>

      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">3. Origem das Anomalias</h2>
        </div>

        <div class="section-body">
          <b-row>
            <b-col lg="6" cols="12" class="mb-3 mb-lg-0">
              <div class="chart-box">
                <apexchart
                    type="pie"
                    height="300"
                    :options="originChartOptions"
                    :series="originChartSeries"
                />
              </div>
            </b-col>

            <b-col lg="6" cols="12">
              <div class="table-responsive">
                <table class="table table-bordered align-middle mb-0">
                  <thead>
                  <tr>
                    <th>Origem</th>
                    <th class="text-right">Quantidade</th>
                    <th class="text-right">%</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in originTable" :key="item.name">
                    <td>{{ item.name }}</td>
                    <td class="text-right">{{ item.value }}</td>
                    <td class="text-right">{{ item.percent }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </b-col>
          </b-row>
        </div>
      </section>

      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">4. Apontamentos por Disciplina Técnica</h2>
        </div>

        <div class="section-body">
          <b-row>
            <b-col lg="7" cols="12" class="mb-3 mb-lg-0">
              <div class="chart-box">
                <apexchart
                    type="bar"
                    height="320"
                    :options="disciplineChartOptions"
                    :series="disciplineChartSeries"
                />
              </div>
            </b-col>

            <b-col lg="5" cols="12">
              <div class="table-responsive">
                <table class="table table-bordered align-middle mb-0">
                  <thead>
                  <tr>
                    <th>Disciplina</th>
                    <th class="text-right">Quantidade</th>
                    <th class="text-right">%</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in disciplineTable" :key="item.name">
                    <td>{{ item.name }}</td>
                    <td class="text-right">{{ item.value }}</td>
                    <td class="text-right">{{ item.percent }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </b-col>
          </b-row>
        </div>
      </section>

      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">5. Ranking de Unidades com Mais Problemas</h2>
        </div>

        <div class="section-body">
          <b-row>
            <b-col lg="7" cols="12" class="mb-3 mb-lg-0">
              <div class="chart-box">
                <apexchart
                    type="bar"
                    height="320"
                    :options="unitsChartOptions"
                    :series="unitsChartSeries"
                />
              </div>
            </b-col>

            <b-col lg="5" cols="12">
              <div class="table-responsive">
                <table class="table table-bordered align-middle mb-0">
                  <thead>
                  <tr>
                    <th>Unidade</th>
                    <th class="text-right">Apontamentos</th>
                    <th class="text-right">Críticos</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in unitsTable" :key="item.name">
                    <td>{{ item.name }}</td>
                    <td class="text-right">{{ item.value }}</td>
                    <td class="text-right">{{ item.critical }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="text-small-muted mt-2">
                O gráfico destaca o volume total de apontamentos por unidade.
              </div>
            </b-col>
          </b-row>
        </div>
      </section>

      <section class="section-card">
        <div class="section-header">
          <h2 class="section-title">6. Top 10 Anomalias Mais Frequentes</h2>
        </div>

        <div class="section-body">
          <b-row>
            <b-col lg="7" cols="12" class="mb-3 mb-lg-0">
              <div class="chart-box">
                <apexchart
                    type="bar"
                    height="320"
                    :options="anomaliesChartOptions"
                    :series="anomaliesChartSeries"
                />
              </div>
            </b-col>

            <b-col lg="5" cols="12">
              <div class="table-responsive">
                <table class="table table-bordered align-middle mb-0">
                  <thead>
                  <tr>
                    <th>Anomalia</th>
                    <th class="text-right">Quantidade</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in anomaliesTable" :key="item.name">
                    <td>{{ item.name }}</td>
                    <td class="text-right">{{ item.value }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="text-small-muted mt-2">
                Exemplo resumido com as ocorrências mais recorrentes.
              </div>
            </b-col>
          </b-row>
        </div>
      </section>

      <footer class="report-footer">
        Indicadores de Qualidade - Exemplo
      </footer>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'QualityIndicatorsReport',
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {
      logoSrc: '/logo.png',

      overview: {
        total: '3.420',
        resolved: '2.610',
        open: '810',
        rate: '76%',
        avgDays: '18 dias',
        avgCriticalDays: '7 dias'
      },

      riskTable: [
        { name: 'Crítico', value: 180, percent: '5%', badgeClass: 'badge-critical' },
        { name: 'Alto', value: 620, percent: '18%', badgeClass: 'badge-high' },
        { name: 'Médio', value: 1540, percent: '45%', badgeClass: 'badge-medium' },
        { name: 'Baixo', value: 1080, percent: '32%', badgeClass: 'badge-low' }
      ],

      originTable: [
        { name: 'Endógena (falha construtiva)', value: 1210, percent: '35%' },
        { name: 'Exógena (uso inadequado / ambiente)', value: 840, percent: '24%' },
        { name: 'Desgaste natural', value: 1050, percent: '31%' },
        { name: 'Falha de manutenção', value: 320, percent: '10%' }
      ],

      disciplineTable: [
        { name: 'Civil', value: 1620, percent: '47%' },
        { name: 'Elétrica', value: 540, percent: '16%' },
        { name: 'Hidráulica', value: 410, percent: '12%' },
        { name: 'Estrutural', value: 220, percent: '6%' },
        { name: 'Segurança / Incêndio', value: 330, percent: '10%' },
        { name: 'Outros', value: 300, percent: '9%' }
      ],

      unitsTable: [
        { name: 'Unidade Centro', value: 320, critical: 18 },
        { name: 'Unidade Norte', value: 280, critical: 12 },
        { name: 'Unidade Sul', value: 260, critical: 9 },
        { name: 'Unidade Leste', value: 210, critical: 6 }
      ],

      anomaliesTable: [
        { name: 'Infiltração', value: 320 },
        { name: 'Fissura em parede', value: 280 },
        { name: 'Falha iluminação', value: 210 },
        { name: 'Vazamento hidráulico', value: 180 },
        { name: 'Revestimento solto', value: 150 }
      ]
    }
  },
  computed: {
    riskChartSeries() {
      return this.riskTable.map(item => item.value)
    },
    riskChartOptions() {
      return {
        chart: {
          type: 'pie',
          toolbar: { show: false }
        },
        labels: this.riskTable.map(item => item.name),
        colors: ['#dc3545', '#fd7e14', '#ffc107', '#28a745'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: '600'
          }
        },
        stroke: {
          colors: ['#ffffff']
        }
      }
    },

    originChartSeries() {
      return this.originTable.map(item => item.value)
    },
    originChartOptions() {
      return {
        chart: {
          type: 'pie',
          toolbar: { show: false }
        },
        labels: this.originTable.map(item => item.name),
        colors: ['#1f77b4', '#17a2b8', '#6f42c1', '#6c757d'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: '600'
          }
        },
        stroke: {
          colors: ['#ffffff']
        }
      }
    },

    disciplineChartSeries() {
      return [
        {
          name: 'Quantidade',
          data: this.disciplineTable.map(item => item.value)
        }
      ]
    },
    disciplineChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        colors: ['#1f3c88'],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            barHeight: '60%'
          }
        },
        dataLabels: {
          enabled: true
        },
        xaxis: {
          categories: this.disciplineTable.map(item => item.name)
        },
        grid: {
          borderColor: '#e5e7eb'
        }
      }
    },

    unitsChartSeries() {
      return [
        {
          name: 'Apontamentos',
          data: this.unitsTable.map(item => item.value)
        }
      ]
    },
    unitsChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        colors: ['#0d6efd'],
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6,
            columnWidth: '45%'
          }
        },
        dataLabels: {
          enabled: true
        },
        xaxis: {
          categories: this.unitsTable.map(item => item.name)
        },
        grid: {
          borderColor: '#e5e7eb'
        }
      }
    },

    anomaliesChartSeries() {
      return [
        {
          name: 'Quantidade',
          data: this.anomaliesTable.map(item => item.value)
        }
      ]
    },
    anomaliesChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        colors: ['#20c997'],
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6,
            columnWidth: '50%'
          }
        },
        dataLabels: {
          enabled: true
        },
        xaxis: {
          categories: this.anomaliesTable.map(item => item.name),
          labels: {
            rotate: -20
          }
        },
        grid: {
          borderColor: '#e5e7eb'
        }
      }
    }
  }
}
</script>

<style scoped>
:root {
  --primary: #1f3c88;
  --secondary: #5b6b8c;
  --soft: #f4f7fb;
  --border: #d9e1ec;
  --critical: #dc3545;
  --high: #fd7e14;
  --medium: #ffc107;
  --low: #28a745;
}

.quality-report-page {
  background: #e9eef5;
  padding: 16px 0;
}

.report-sheet {
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  padding: 14mm 12mm 14mm 12mm;
}

.report-header {
  border-bottom: 2px solid var(--primary);
  padding-bottom: 14px;
  margin-bottom: 20px;
}

.report-logo {
  max-height: 56px;
  max-width: 180px;
  object-fit: contain;
}

.report-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--primary);
  margin: 0;
  line-height: 1.2;
}

.report-subtitle {
  font-size: 14px;
  color: var(--secondary);
  margin-top: 4px;
}

.section-card {
  border: 1px solid var(--border);
  border-radius: 14px;
  margin-bottom: 18px;
  overflow: hidden;
  break-inside: avoid;
  page-break-inside: avoid;
}

.section-header {
  background: linear-gradient(90deg, #f8fbff 0%, #eef4fb 100%);
  border-bottom: 1px solid var(--border);
  padding: 12px 16px;
}

.section-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--primary);
}

.section-body {
  padding: 16px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 12px;
}

.summary-box {
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--soft);
  padding: 14px;
  min-height: 92px;
}

.summary-label {
  font-size: 13px;
  color: var(--secondary);
  margin-bottom: 6px;
}

.summary-value {
  font-size: 26px;
  font-weight: 700;
  color: var(--primary);
  line-height: 1.1;
}

.summary-note {
  font-size: 12px;
  color: #667085;
  margin-top: 4px;
}

.table thead th {
  background: #f3f6fa;
  color: #334155;
  font-size: 13px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}

.table td {
  vertical-align: middle;
  font-size: 13px;
}

.chart-box {
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 10px;
  background: #fff;
  min-height: 320px;
}

.mini-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
}

.badge-critical {
  background: var(--critical);
}

.badge-high {
  background: var(--high);
}

.badge-medium {
  background: #d39e00;
}

.badge-low {
  background: var(--low);
}

.report-footer {
  margin-top: 26px;
  padding-top: 12px;
  border-top: 2px solid var(--primary);
  text-align: center;
  color: var(--secondary);
  font-size: 13px;
  font-weight: 600;
}

.text-small-muted {
  font-size: 12px;
  color: #6b7280;
}

@media (max-width: 1100px) {
  .report-sheet {
    width: 100%;
    min-height: auto;
    box-shadow: none;
    padding: 20px;
  }

  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .report-title {
    font-size: 22px;
  }
}

@page {
  size: A4;
  margin: 10mm;
}

@media print {
  .quality-report-page {
    background: #fff;
    padding: 0;
  }

  .report-sheet {
    width: 100%;
    min-height: auto;
    margin: 0;
    box-shadow: none;
    padding: 0;
  }

  .section-card,
  .row,
  .col-lg-6,
  .col-lg-7,
  .col-lg-5,
  .col-12 {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .chart-box {
    min-height: 280px;
  }
}
</style>